import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePermissionModal } from 'contexts/PermissionModalContext';
import * as nfeService from 'services/nfeService';
import type {
  NFeFiltros,
  CriarRascunhoNFeRequest,
  CriarRascunhoNFeResponse,
  AtualizarCabecalhoRequest,
  AtualizarDestinatarioRequest,
  AdicionarItemRequest,
  AdicionarItemComProdutoRequest,
  AtualizarItemRequest,
  AtualizarTributacaoItemRequest,
  AdicionarDuplicataRequest,
  AtualizarDuplicataRequest,
  AtualizarTransporteRequest,
  AtualizarInformacoesAdicionaisRequest,
  CancelarNFeRequest,
  BuscarProdutosNFeItem,
  ProdutoFiscalNFeResponse,
  RegraFiscalNFeItem,
} from '../types/nfe';
import type { AdicionarReferenciaImportadaResponse, EmitirCartaCorrecaoRequest } from '../services/nfeService';

// Query keys hierarchy
export const nfeKeys = {
  all: ['nfe'] as const,
  lists: () => [...nfeKeys.all, 'list'] as const,
  list: (filtros: NFeFiltros) => [...nfeKeys.lists(), filtros] as const,
  details: () => [...nfeKeys.all, 'detail'] as const,
  detail: (id: string) => [...nfeKeys.details(), id] as const,
  eventos: (id: string) => [...nfeKeys.all, 'eventos', id] as const,
};

export const useNFes = (filtros: NFeFiltros) => {
  const { getQueryRetryHandler } = usePermissionModal();

  return useQuery({
    queryKey: nfeKeys.list(filtros),
    queryFn: () => nfeService.listarNFes(filtros),
    retry: getQueryRetryHandler({
      queryKey: nfeKeys.list(filtros),
      defaultMessage: 'Você não possui permissão para consultar NFes.',
    }),
  });
};

export const useNFe = (id: string | undefined, enabled: boolean = true) => {
  const { getQueryRetryHandler } = usePermissionModal();

  return useQuery({
    queryKey: nfeKeys.detail(id ?? ''),
    queryFn: () => nfeService.obterNFe(id!),
    enabled: !!id && enabled,
    retry: getQueryRetryHandler({
      queryKey: nfeKeys.detail(id ?? ''),
      defaultMessage: 'Você não possui permissão para consultar esta NFe.',
    }),
  });
};

export const useCriarRascunhoNFe = () => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (request: CriarRascunhoNFeRequest) => nfeService.criarRascunhoNFe(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nfeKeys.lists() });
    },
    onError: getMutationErrorHandler('Você não possui permissão para criar NF-e.'),
  });
};

export const useExcluirNFe = () => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (id: string) => nfeService.excluirNFe(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nfeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: nfeKeys.details() });
    },
    onError: getMutationErrorHandler('Você não possui permissão para excluir NF-e.'),
  });
};

export const useAtualizarCabecalho = (nfeId: string) => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (request: AtualizarCabecalhoRequest) =>
      nfeService.atualizarCabecalho(nfeId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nfeKeys.detail(nfeId) });
      queryClient.invalidateQueries({ queryKey: nfeKeys.lists() });
    },
    onError: getMutationErrorHandler('Você não possui permissão para editar NF-e.'),
  });
};

export const useAtualizarDestinatario = (nfeId: string) => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (request: AtualizarDestinatarioRequest) =>
      nfeService.atualizarDestinatario(nfeId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nfeKeys.detail(nfeId) });
      queryClient.invalidateQueries({ queryKey: nfeKeys.lists() });
    },
    onError: getMutationErrorHandler('Você não possui permissão para editar NF-e.'),
  });
};

export const useAdicionarItem = (nfeId: string) => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (request: AdicionarItemRequest) =>
      nfeService.adicionarItem(nfeId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nfeKeys.detail(nfeId) });
      queryClient.invalidateQueries({ queryKey: nfeKeys.lists() });
    },
    onError: getMutationErrorHandler('Você não possui permissão para editar NF-e.'),
  });
};

export const useAdicionarItemAvulso = (nfeId: string) => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (request: AdicionarItemRequest) =>
      nfeService.adicionarItemAvulso(nfeId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nfeKeys.detail(nfeId) });
      queryClient.invalidateQueries({ queryKey: nfeKeys.lists() });
    },
    onError: getMutationErrorHandler('Você não possui permissão para editar NF-e.'),
  });
};

export const useAdicionarItemComProduto = (nfeId: string) => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (request: AdicionarItemComProdutoRequest) =>
      nfeService.adicionarItemComProduto(nfeId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nfeKeys.detail(nfeId) });
      queryClient.invalidateQueries({ queryKey: nfeKeys.lists() });
    },
    onError: getMutationErrorHandler('Você não possui permissão para editar NF-e.'),
  });
};

export const useAtualizarItem = (nfeId: string, itemId: string) => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (request: AtualizarItemRequest) =>
      nfeService.atualizarItem(nfeId, itemId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nfeKeys.detail(nfeId) });
      queryClient.invalidateQueries({ queryKey: nfeKeys.lists() });
    },
    onError: getMutationErrorHandler('Você não possui permissão para editar NF-e.'),
  });
};

export const useAtualizarTributacaoItem = (nfeId: string, itemId: string) => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (request: AtualizarTributacaoItemRequest) =>
      nfeService.atualizarTributacaoItem(nfeId, itemId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nfeKeys.detail(nfeId) });
    },
    onError: getMutationErrorHandler('Você não possui permissão para editar NF-e.'),
  });
};

export const useExcluirItem = (nfeId: string) => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (itemId: string) => nfeService.excluirItem(nfeId, itemId),
    onMutate: async (itemId) => {
      await queryClient.cancelQueries({ queryKey: nfeKeys.detail(nfeId) });
      const previous = queryClient.getQueryData(nfeKeys.detail(nfeId));
      queryClient.setQueryData(nfeKeys.detail(nfeId), (old: any) => {
        if (!old) return old;
        return { ...old, itens: old.itens?.filter((i: any) => i.id !== itemId) || [] };
      });
      return { previous };
    },
    onError: (err, _itemId, context: any) => {
      if (context?.previous) {
        queryClient.setQueryData(nfeKeys.detail(nfeId), context.previous);
      }
      getMutationErrorHandler('Você não possui permissão para editar NF-e.')(err);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: nfeKeys.detail(nfeId) });
      queryClient.invalidateQueries({ queryKey: nfeKeys.lists() });
    },
  });
};

export const useAdicionarDuplicata = (nfeId: string) => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (request: AdicionarDuplicataRequest) =>
      nfeService.adicionarDuplicata(nfeId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nfeKeys.detail(nfeId) });
      queryClient.invalidateQueries({ queryKey: nfeKeys.lists() });
    },
    onError: getMutationErrorHandler('Você não possui permissão para editar NF-e.'),
  });
};

export const useAtualizarDuplicata = (nfeId: string, duplicataId: string) => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (request: AtualizarDuplicataRequest) =>
      nfeService.atualizarDuplicata(nfeId, duplicataId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nfeKeys.detail(nfeId) });
      queryClient.invalidateQueries({ queryKey: nfeKeys.lists() });
    },
    onError: getMutationErrorHandler('Você não possui permissão para editar NF-e.'),
  });
};

export const useExcluirDuplicata = (nfeId: string) => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (duplicataId: string) =>
      nfeService.excluirDuplicata(nfeId, duplicataId),
    onMutate: async (duplicataId) => {
      await queryClient.cancelQueries({ queryKey: nfeKeys.detail(nfeId) });
      const previous = queryClient.getQueryData(nfeKeys.detail(nfeId));
      queryClient.setQueryData(nfeKeys.detail(nfeId), (old: any) => {
        if (!old) return old;
        return { ...old, duplicatas: old.duplicatas?.filter((d: any) => d.id !== duplicataId) || [] };
      });
      return { previous };
    },
    onError: (err, _duplicataId, context: any) => {
      if (context?.previous) {
        queryClient.setQueryData(nfeKeys.detail(nfeId), context.previous);
      }
      getMutationErrorHandler('Você não possui permissão para editar NF-e.')(err);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: nfeKeys.detail(nfeId) });
      queryClient.invalidateQueries({ queryKey: nfeKeys.lists() });
    },
  });
};

export const useAtualizarTransporte = (nfeId: string) => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (request: AtualizarTransporteRequest) =>
      nfeService.atualizarTransporte(nfeId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nfeKeys.detail(nfeId) });
    },
    onError: getMutationErrorHandler('Você não possui permissão para editar NF-e.'),
  });
};

export const useAtualizarInformacoesAdicionais = (nfeId: string) => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (request: AtualizarInformacoesAdicionaisRequest) =>
      nfeService.atualizarInformacoesAdicionais(nfeId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nfeKeys.detail(nfeId) });
    },
    onError: getMutationErrorHandler('Você não possui permissão para editar NF-e.'),
  });
};

export const useTransmitirNFe = (emissaoId: string) => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation({
    mutationFn: () => nfeService.transmitirNFe(emissaoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nfe', emissaoId] });
      queryClient.invalidateQueries({ queryKey: nfeKeys.lists() });
    },
    onError: getMutationErrorHandler('Você não possui permissão para transmitir NF-e.'),
  });
};

export const useCancelarNFe = (emissaoId: string) => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (request: CancelarNFeRequest) => nfeService.cancelarNFe(emissaoId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nfe', emissaoId] });
      queryClient.invalidateQueries({ queryKey: nfeKeys.lists() });
    },
    onError: getMutationErrorHandler('Você não possui permissão para cancelar NF-e.'),
  });
};

export const useAdicionarReferencia = (emissaoId: string) => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (chave: string) =>
      nfeService.adicionarReferencia(emissaoId, { chaveNFeReferenciada: chave }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nfeKeys.detail(emissaoId) });
    },
    onError: getMutationErrorHandler('Você não possui permissão para editar NF-e.'),
  });
};

export const useRemoverReferencia = (emissaoId: string) => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (refId: string) => nfeService.removerReferencia(emissaoId, refId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nfeKeys.detail(emissaoId) });
    },
    onError: getMutationErrorHandler('Você não possui permissão para editar NF-e.'),
  });
};

export const useBuscarProdutosNFe = (filter: string, enabled: boolean) =>
  useQuery<BuscarProdutosNFeItem[]>({
    queryKey: ['nfe', 'produtos', filter],
    queryFn: () => nfeService.buscarProdutos(filter),
    enabled: enabled && filter.length >= 2,
    staleTime: 30_000,
  });

export const useObterProdutoFiscalNFe = (produtoId: string, empresaId: string, enabled: boolean) =>
  useQuery<ProdutoFiscalNFeResponse>({
    queryKey: ['nfe', 'produto-fiscal', produtoId, empresaId],
    queryFn: () => nfeService.obterProdutoFiscal(produtoId, empresaId),
    enabled: enabled && !!produtoId && !!empresaId,
    staleTime: 60_000,
  });

export const useBuscarRegrasFiscaisNFe = (filter: string, enabled: boolean) =>
  useQuery<RegraFiscalNFeItem[]>({
    queryKey: ['nfe', 'regras-fiscais', filter],
    queryFn: () => nfeService.buscarRegrasFiscais(filter),
    enabled,
    staleTime: 30_000,
  });

export const useImportarXmlNFe = () => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation<CriarRascunhoNFeResponse, Error, { xml: File; empresaId: string }>({
    mutationFn: ({ xml, empresaId }) => nfeService.importarXmlNFe(xml, empresaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nfeKeys.all });
    },
    onError: getMutationErrorHandler('Você não possui permissão para importar NF-e.'),
  });
};

export const useImportarPorChaveNFe = () => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation<CriarRascunhoNFeResponse, Error, { chaveAcesso: string; empresaId: string }>({
    mutationFn: ({ chaveAcesso, empresaId }) => nfeService.importarPorChaveNFe(chaveAcesso, empresaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nfeKeys.all });
    },
    onError: getMutationErrorHandler('Você não possui permissão para importar NF-e.'),
  });
};

export const useAdicionarReferenciaImportadaXml = (emissaoId: string) => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (xml: File) =>
      nfeService.adicionarReferenciaImportadaXml(emissaoId, xml),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nfeKeys.detail(emissaoId) });
    },
    onError: getMutationErrorHandler('Você não possui permissão para editar NF-e.'),
  });
};

export const useEventosNFe = (emissaoId: string, enabled: boolean = false) => {
  const { getQueryRetryHandler } = usePermissionModal();

  return useQuery({
    queryKey: nfeKeys.eventos(emissaoId),
    queryFn: () => nfeService.obterEventosNFe(emissaoId),
    enabled: !!emissaoId && enabled,
    retry: getQueryRetryHandler({
      queryKey: nfeKeys.eventos(emissaoId),
      defaultMessage: 'Você não possui permissão para consultar eventos da NF-e.',
    }),
  });
};

export const useEmitirCartaCorrecaoNFe = (emissaoId: string) => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (request: EmitirCartaCorrecaoRequest) =>
      nfeService.emitirCartaCorrecaoNFe(emissaoId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nfeKeys.eventos(emissaoId) });
    },
    onError: getMutationErrorHandler('Você não possui permissão para emitir Carta de Correção.'),
  });
};

export const useAdicionarReferenciaImportadaChave = (emissaoId: string) => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (chaveAcesso: string) =>
      nfeService.adicionarReferenciaImportadaChave(emissaoId, chaveAcesso),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nfeKeys.detail(emissaoId) });
    },
    onError: getMutationErrorHandler('Você não possui permissão para editar NF-e.'),
  });
};
